/**
 * FADO Theme JavaScript
 * Vanilla JS for Shopify theme functionality
 */

const FadoTheme = {
  init() {
    this.initNavbar();
    this.initMobileMenu();
    this.initCartDrawer();
    this.initQuantitySelectors();
    this.initAccordions();
    this.initProductGallery();
    this.initQuickAdd();
    this.initNewsletterForm();
    this.initAnimateOnScroll();
    this.initVariantSelector();
    this.initAddToCartForms();
  },

  // ============================================
  // NAVBAR - Scroll behavior
  // ============================================
  initNavbar() {
    const navbar = document.querySelector('[data-navbar]');
    if (!navbar) return;

    const updateNavbar = () => {
      const alwaysSolid = navbar.dataset.alwaysSolid === 'true';
      const isScrolled = window.scrollY > 50;
      const showSolid = isScrolled || alwaysSolid;

      if (showSolid) {
        navbar.classList.add('navbar-solid');
        navbar.classList.remove('navbar-transparent');
      } else {
        navbar.classList.remove('navbar-solid');
        navbar.classList.add('navbar-transparent');
      }
    };

    window.addEventListener('scroll', updateNavbar, { passive: true });
    updateNavbar();
  },

  // ============================================
  // MOBILE MENU DRAWER
  // ============================================
  initMobileMenu() {
    const triggers = document.querySelectorAll('[data-mobile-menu-trigger]');
    const drawer = document.querySelector('[data-mobile-menu-drawer]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');
    const closeBtn = document.querySelector('[data-mobile-menu-close]');

    if (!drawer) return;

    const openMenu = () => {
      drawer.setAttribute('data-state', 'open');
      overlay?.setAttribute('data-state', 'open');
      document.body.style.overflow = 'hidden';
    };

    const closeMenu = () => {
      drawer.setAttribute('data-state', 'closed');
      overlay?.setAttribute('data-state', 'closed');
      document.body.style.overflow = '';
    };

    triggers.forEach(trigger => {
      trigger.addEventListener('click', openMenu);
    });

    closeBtn?.addEventListener('click', closeMenu);
    overlay?.addEventListener('click', closeMenu);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('data-state') === 'open') {
        closeMenu();
      }
    });
  },

  // ============================================
  // CART DRAWER
  // ============================================
  initCartDrawer() {
    const triggers = document.querySelectorAll('[data-cart-trigger]');
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    const closeBtn = document.querySelector('[data-cart-close]');

    if (!drawer) return;

    const openCart = () => {
      drawer.setAttribute('data-state', 'open');
      overlay?.setAttribute('data-state', 'open');
      document.body.style.overflow = 'hidden';
      this.refreshCart();
    };

    const closeCart = () => {
      drawer.setAttribute('data-state', 'closed');
      overlay?.setAttribute('data-state', 'closed');
      document.body.style.overflow = '';
    };

    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        openCart();
      });
    });

    closeBtn?.addEventListener('click', closeCart);
    overlay?.addEventListener('click', closeCart);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('data-state') === 'open') {
        closeCart();
      }
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.refreshCart();
    });
  },

  async refreshCart() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      const cart = await response.json();
      this.updateCartUI(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  },

  updateCartUI(cart) {
    // Update cart count badges
    const countElements = document.querySelectorAll('[data-cart-count]');
    countElements.forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });

    // Update cart drawer content
    const cartContent = document.querySelector('[data-cart-content]');
    if (cartContent) {
      this.renderCartContent(cartContent, cart);
    }

    // Update cart drawer footer
    const cartFooter = document.querySelector('[data-cart-footer]');
    if (cartFooter) {
      if (cart.item_count > 0) {
        cartFooter.style.display = '';
        const subtotalEl = cartFooter.querySelector('[data-cart-subtotal]');
        if (subtotalEl) {
          subtotalEl.textContent = this.formatMoney(cart.total_price);
        }
      } else {
        cartFooter.style.display = 'none';
      }
    }
  },

  formatMoney(cents) {
    // Basic money formatting - adjust currency symbol as needed
    const amount = (cents / 100).toFixed(2);
    return window.Shopify?.currency?.active
      ? new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify.currency.active }).format(cents / 100)
      : '₹' + amount;
  },

  renderCartContent(container, cart) {
    if (cart.item_count === 0) {
      container.innerHTML = `
        <div class="text-center py-12">
          <p class="text-muted-foreground mb-6">Your cart is empty</p>
          <a href="/collections" class="btn btn-outline h-12 px-8 uppercase tracking-widest text-xs font-bold">
            Continue Shopping
          </a>
        </div>
      `;
      return;
    }

    const itemsHtml = cart.items.map(item => {
      const imageUrl = item.image ? item.image.replace(/(\.[^.]+)$/, '_160x$1') : '';
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="text-xs text-muted-foreground mt-1">${item.variant_title}</p>`
        : '';

      return `
        <div class="flex gap-4" data-cart-item data-line-key="${item.key}">
          <div class="w-20 h-24 bg-muted overflow-hidden flex-shrink-0">
            ${imageUrl ? `<img src="${imageUrl}" alt="${item.title}" class="w-full h-full object-cover" loading="lazy">` : ''}
          </div>
          <div class="flex-1 min-w-0">
            <a href="${item.url}" class="font-serif text-sm font-medium hover:text-primary line-clamp-2">
              ${item.product_title}
            </a>
            ${variantTitle}
            <p class="text-sm font-medium mt-1">${this.formatMoney(item.final_line_price)}</p>
            <div class="flex items-center justify-between mt-2">
              <div data-quantity-selector class="quantity-selector text-xs">
                <button data-quantity-minus type="button" aria-label="Decrease quantity">-</button>
                <input
                  data-quantity-input
                  type="number"
                  value="${item.quantity}"
                  min="0"
                  max="999"
                  data-line-key="${item.key}"
                  aria-label="Quantity"
                >
                <button data-quantity-plus type="button" aria-label="Increase quantity">+</button>
              </div>
              <button
                class="nav-icon-btn text-xs text-muted-foreground hover:text-destructive transition-colors underline cursor-pointer"
                onclick="FadoTheme.removeCartItem('${item.key}')"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = `<div class="space-y-6">${itemsHtml}</div>`;

    // Re-initialize quantity selectors for the new content
    this.initQuantitySelectors();
    this.initCartItemQuantityChange();
  },

  initCartItemQuantityChange() {
    const inputs = document.querySelectorAll('[data-cart-content] [data-quantity-input]');
    inputs.forEach(input => {
      input.addEventListener('change', async (e) => {
        const lineKey = input.dataset.lineKey;
        const quantity = parseInt(input.value) || 0;
        if (lineKey) {
          await this.updateCartItem(lineKey, quantity);
        }
      });
    });
  },

  // ============================================
  // QUANTITY SELECTORS
  // ============================================
  initQuantitySelectors() {
    const selectors = document.querySelectorAll('[data-quantity-selector]');

    selectors.forEach(selector => {
      const minusBtn = selector.querySelector('[data-quantity-minus]');
      const plusBtn = selector.querySelector('[data-quantity-plus]');
      const input = selector.querySelector('[data-quantity-input]');

      if (!input) return;

      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 999;

      const clampValue = (newValue) => {
        return Math.max(min, Math.min(max, newValue));
      };

      minusBtn?.addEventListener('click', () => {
        input.value = clampValue(parseInt(input.value) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      plusBtn?.addEventListener('click', () => {
        input.value = clampValue(parseInt(input.value) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      input.addEventListener('change', () => {
        input.value = clampValue(parseInt(input.value) || min);
      });
    });
  },

  // ============================================
  // ACCORDIONS
  // ============================================
  initAccordions() {
    const accordions = document.querySelectorAll('[data-accordion]');

    accordions.forEach(accordion => {
      const triggers = accordion.querySelectorAll('[data-accordion-trigger]');
      const singleOpen = accordion.dataset.accordion === 'single';

      triggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const item = trigger.closest('[data-accordion-item]');
          const content = item?.querySelector('[data-accordion-content]');
          const isOpen = trigger.getAttribute('data-state') === 'open';

          if (singleOpen) {
            // Close all other items
            accordion.querySelectorAll('[data-accordion-trigger]').forEach(t => {
              if (t !== trigger) {
                t.setAttribute('data-state', 'closed');
                const c = t.closest('[data-accordion-item]')?.querySelector('[data-accordion-content]');
                c?.setAttribute('data-state', 'closed');
              }
            });
          }

          // Toggle current item
          const newState = isOpen ? 'closed' : 'open';
          trigger.setAttribute('data-state', newState);
          content?.setAttribute('data-state', newState);
        });
      });
    });
  },

  // ============================================
  // PRODUCT GALLERY
  // ============================================
  initProductGallery() {
    const galleries = document.querySelectorAll('[data-product-gallery]');

    galleries.forEach(gallery => {
      const mainImage = gallery.querySelector('[data-main-image]');
      const thumbnails = gallery.querySelectorAll('[data-thumbnail]');

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const imgSrc = thumb.dataset.imageSrc;
          const imgAlt = thumb.dataset.imageAlt || '';

          if (mainImage && imgSrc) {
            mainImage.src = imgSrc;
            mainImage.alt = imgAlt;
          }

          // Update active state
          thumbnails.forEach(t => t.classList.remove('thumbnail-active'));
          thumb.classList.add('thumbnail-active');
        });
      });
    });
  },

  // ============================================
  // QUICK ADD TO CART
  // ============================================
  initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const quickAddBtn = e.target.closest('[data-quick-add]');
      if (!quickAddBtn) return;

      e.preventDefault();
      const variantId = quickAddBtn.dataset.variantId;
      const quantity = quickAddBtn.dataset.quantity || 1;

      if (!variantId) return;

      quickAddBtn.disabled = true;
      quickAddBtn.classList.add('opacity-50');

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: parseInt(quantity)
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const addedData = await response.json();
        document.dispatchEvent(new CustomEvent('cart:added', {
          detail: {
            variantId: addedData.variant_id,
            productTitle: addedData.product_title,
            price: addedData.price,
            quantity: addedData.quantity,
            productId: addedData.product_id,
            sku: addedData.sku
          }
        }));

        // Refresh cart and open drawer
        await this.refreshCart();

        // Open cart drawer
        const cartDrawer = document.querySelector('[data-cart-drawer]');
        const cartOverlay = document.querySelector('[data-cart-overlay]');
        if (cartDrawer) {
          cartDrawer.setAttribute('data-state', 'open');
          cartOverlay?.setAttribute('data-state', 'open');
          document.body.style.overflow = 'hidden';
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        alert('Could not add item to cart. Please try again.');
      } finally {
        quickAddBtn.disabled = false;
        quickAddBtn.classList.remove('opacity-50');
      }
    });
  },

  // ============================================
  // VARIANT SELECTOR - Update price & variant ID
  // ============================================
  initVariantSelector() {
    const productJsonEl = document.querySelector('[data-product-json]');
    if (!productJsonEl) return;

    const product = JSON.parse(productJsonEl.textContent);
    const variants = product.variants;
    const form = document.querySelector('[data-add-to-cart-form]');
    if (!form) return;

    const optionSelectors = form.querySelectorAll('[data-option-selector]');
    if (!optionSelectors.length) return;

    const getSelectedOptions = () =>
      Array.from(optionSelectors).map(s => s.value);

    const findVariant = (selectedOptions) =>
      variants.find(v =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      );

    const updateUI = (variant) => {
      if (!variant) return;

      // Update hidden variant ID input
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;

      // Format price using Shopify's money format
      const formatMoney = (cents) => {
        const dollars = (cents / 100).toFixed(2);
        return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };

      const priceStr = formatMoney(variant.price);

      // Update both price displays
      const variantPrice = document.querySelector('[data-variant-price]');
      if (variantPrice) variantPrice.textContent = priceStr;

      const btnPrice = form.querySelector('[data-price]');
      if (btnPrice) btnPrice.textContent = priceStr;

      // Update submit button availability
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
      }
    };

    optionSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        const selectedOptions = getSelectedOptions();
        const variant = findVariant(selectedOptions);
        updateUI(variant);
      });
    });
  },

  // ============================================
  // ADD TO CART FORM
  // ============================================
  initAddToCartForms() {
    const forms = document.querySelectorAll('[data-add-to-cart-form]');

    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('[type="submit"]');
        const formData = new FormData(form);

        submitBtn.disabled = true;
        submitBtn.classList.add('opacity-50');

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          const addedData = await response.json();
          document.dispatchEvent(new CustomEvent('cart:added', {
            detail: {
              variantId: addedData.variant_id,
              productTitle: addedData.product_title,
              price: addedData.price,
              quantity: addedData.quantity,
              productId: addedData.product_id,
              sku: addedData.sku
            }
          }));

          document.dispatchEvent(new CustomEvent('cart:updated'));

          // Open cart drawer
          const cartDrawer = document.querySelector('[data-cart-drawer]');
          const cartOverlay = document.querySelector('[data-cart-overlay]');
          if (cartDrawer) {
            cartDrawer.setAttribute('data-state', 'open');
            cartOverlay?.setAttribute('data-state', 'open');
            document.body.style.overflow = 'hidden';
          }

        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('Could not add item to cart. Please try again.');
        } finally {
          submitBtn.disabled = false;
          submitBtn.classList.remove('opacity-50');
        }
      });
    });
  },

  // ============================================
  // NEWSLETTER FORM
  // ============================================
  initNewsletterForm() {
    const forms = document.querySelectorAll('[data-newsletter-form]');

    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        // Let Shopify handle the form submission
        // This is just for any additional client-side handling
        const emailInput = form.querySelector('input[type="email"]');
        if (emailInput && !emailInput.value) {
          e.preventDefault();
          emailInput.focus();
        }
      });
    });
  },

  // ============================================
  // ANIMATE ON SCROLL
  // ============================================
  initAnimateOnScroll() {
    const animateElements = document.querySelectorAll('[data-animate]');

    if (!animateElements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const animation = entry.target.dataset.animate || 'fade-in';
          entry.target.classList.add(`animate-${animation}`);
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    animateElements.forEach(el => observer.observe(el));
  },

  // ============================================
  // CART OPERATIONS
  // ============================================
  async updateCartItem(key, quantity) {
    try {
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      document.dispatchEvent(new CustomEvent('cart:updated'));
      return await response.json();
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  },

  async removeCartItem(key) {
    return this.updateCartItem(key, 0);
  }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FadoTheme.init());
} else {
  FadoTheme.init();
}

// Export for global access
window.FadoTheme = FadoTheme;
